import { AnalysisView } from "@/features/maps";
import { createContext, MutableRefObject, PropsWithChildren, useCallback, useContext, useRef, useState } from "react";
import type { CompareResultsMode } from "../types";
export type TargetRefCallback = (node: HTMLElement | null) => void;
interface CollectionTutorialState {
  isTutorialOpen: boolean;
  tutorialsActiveView: AnalysisView;
  setTutorialsActiveView: (value: AnalysisView) => void;
  selectFirstItemInAssetList: boolean;
  setSelectFirstItemInAssetList: (value: boolean) => void;
  backToAssetList: boolean;
  setBackToAssetList: (value: boolean) => void;
  /**
   * Masks the aggregated view's compare mode while the tour is on a compare step. Read-only:
   * the user's own selection stays untouched underneath and shows through again when null.
   */
  tutorialResultsMode: CompareResultsMode | null;
  setTutorialResultsMode: (value: CompareResultsMode | null) => void;
  /** Highlights the first non-baseline pathway */
  selectFirstPathway: boolean;
  setSelectFirstPathway: (value: boolean) => void;
  viewSwitchTabs: MutableRefObject<null>;
  sidebarInfo: MutableRefObject<null>;
  sidebarDownloads: MutableRefObject<null>;
  pointFilters: MutableRefObject<null>;
  yearsPlayTrigger: MutableRefObject<null>;
  aggregateCompareSelector: MutableRefObject<null>;
  aggregateResultsPanel: MutableRefObject<null>;
  bindAggregateResultsPanel: TargetRefCallback;
  riskAssetSearch: MutableRefObject<null>;
  riskItemSelect: MutableRefObject<null>;
  riskTabTrigger: MutableRefObject<null>;
  backToAssetListTrigger: MutableRefObject<null>;
  bindBackToAssetListTrigger: TargetRefCallback;
  pointMapPanel: MutableRefObject<null>;
  bindPointMapPanel: TargetRefCallback;
  pointMapToolbar: MutableRefObject<null>;
  bindPointMapToolbar: TargetRefCallback;
  pointCompareToggle: MutableRefObject<null>;
  bindPointCompareToggle: TargetRefCallback;
  adaptationTabTrigger: MutableRefObject<null>;
  adaptationChart: MutableRefObject<null>;
  bindAdaptationChart: TargetRefCallback;
  adaptationListSort: MutableRefObject<null>;
  bindAdaptationListSort: TargetRefCallback;
  adaptationRow: MutableRefObject<null>;
  bindAdaptationRow: TargetRefCallback;
  adaptationPathwaysPanel: MutableRefObject<null>;
  bindAdaptationPathwaysPanel: TargetRefCallback;
  adaptationPathwayCard: MutableRefObject<null>;
  bindAdaptationPathwayCard: TargetRefCallback;
  pointDetailPanel: MutableRefObject<null>;
  pointSettingsPanel: MutableRefObject<null>;
  backToAnalysisListTrigger: MutableRefObject<null>;
}
/**
 * Shared sentinel for every unbound `useTargetRef`. Never pass it to a `ref` prop: React would
 * write that element into it and every other unbound target would resolve to the same node.
 */
const emptyRefObj = {
  current: null
};
const CollectionTutorialContext = createContext<CollectionTutorialState | undefined>(undefined);

/**
 * A tour target that only exists once its element mounts. Tour re-resolves `target()` when it
 * re-renders, and a ref filling in doesn't cause one, so the node arrives via state instead.
 * Swapping between two fixed identities keeps repeat calls from looping, and the callback has
 * to stay stable or React detaches it every render.
 */
const useTargetRef = (): readonly [MutableRefObject<null>, TargetRefCallback] => {
  const ref = useRef<HTMLElement | null>(null);
  const [target, setTarget] = useState<MutableRefObject<null>>(emptyRefObj);
  const bind = useCallback<TargetRefCallback>(node => {
    ref.current = node;
    setTarget(node ? ref as MutableRefObject<null> : emptyRefObj);
  }, []);
  return [target, bind] as const;
};
export const CollectionTutorialProvider = ({
  isTutorialOpen,
  children
}: PropsWithChildren & {
  isTutorialOpen: boolean;
}) => {
  const viewSwitchTabsRef = useRef(null);
  const sidebarInfoRef = useRef(null);
  const sidebarDownloadsRef = useRef(null);
  const pointFiltersRef = useRef(null);
  const yearsPlayTriggerRef = useRef(null);
  const aggregateCompareSelectorRef = useRef(null);
  const [aggregateResultsPanel, bindAggregateResultsPanel] = useTargetRef();
  const riskAssetSearchRef = useRef(null);
  const riskItemSelectRef = useRef(null);
  const riskTabTriggerRef = useRef(null);
  const [pointMapPanel, bindPointMapPanel] = useTargetRef();
  const [pointMapToolbar, bindPointMapToolbar] = useTargetRef();
  const [pointCompareToggle, bindPointCompareToggle] = useTargetRef();
  const adaptationTabTriggerRef = useRef(null);
  const [adaptationChart, bindAdaptationChart] = useTargetRef();
  const [adaptationListSort, bindAdaptationListSort] = useTargetRef();
  const [adaptationRow, bindAdaptationRow] = useTargetRef();
  const [adaptationPathwaysPanel, bindAdaptationPathwaysPanel] = useTargetRef();
  const [adaptationPathwayCard, bindAdaptationPathwayCard] = useTargetRef();
  const pointDetailPanelRef = useRef(null);
  const pointSettingsPanelRef = useRef(null);
  const [tutorialsActiveView, setTutorialsActiveView] = useState<AnalysisView>("portfolio");
  const [selectFirstItemInAssetList, setSelectFirstItemInAssetList] = useState(false);
  const [tutorialResultsMode, setTutorialResultsMode] = useState<CompareResultsMode | null>(null);
  const [selectFirstPathway, setSelectFirstPathway] = useState(false);
  const backToAnalysisListTriggerRef = useRef(null);
  const [backToAssetListTrigger, bindBackToAssetListTrigger] = useTargetRef();
  /** Drives the navigation back to the asset list; the button's own target is bound separately. */
  const [backToAssetList, setBackToAssetList] = useState(false);
  return <CollectionTutorialContext.Provider value={{
    isTutorialOpen,
    tutorialsActiveView,
    setTutorialsActiveView,
    selectFirstItemInAssetList,
    setSelectFirstItemInAssetList,
    backToAssetList,
    setBackToAssetList,
    tutorialResultsMode,
    setTutorialResultsMode,
    selectFirstPathway,
    setSelectFirstPathway,
    viewSwitchTabs: viewSwitchTabsRef,
    sidebarInfo: sidebarInfoRef,
    sidebarDownloads: sidebarDownloadsRef,
    pointFilters: pointFiltersRef,
    yearsPlayTrigger: yearsPlayTriggerRef,
    aggregateCompareSelector: aggregateCompareSelectorRef,
    aggregateResultsPanel,
    bindAggregateResultsPanel,
    riskAssetSearch: riskAssetSearchRef,
    riskItemSelect: riskItemSelectRef,
    riskTabTrigger: riskTabTriggerRef,
    pointMapPanel,
    bindPointMapPanel,
    pointMapToolbar,
    bindPointMapToolbar,
    pointCompareToggle,
    bindPointCompareToggle,
    adaptationTabTrigger: adaptationTabTriggerRef,
    adaptationChart,
    bindAdaptationChart,
    adaptationListSort,
    bindAdaptationListSort,
    adaptationRow,
    bindAdaptationRow,
    adaptationPathwaysPanel,
    bindAdaptationPathwaysPanel,
    adaptationPathwayCard,
    bindAdaptationPathwayCard,
    pointDetailPanel: pointDetailPanelRef,
    pointSettingsPanel: pointSettingsPanelRef,
    backToAnalysisListTrigger: backToAnalysisListTriggerRef,
    backToAssetListTrigger,
    bindBackToAssetListTrigger
  }} data-sentry-element="unknown" data-sentry-component="CollectionTutorialProvider" data-sentry-source-file="CollectionTutorialContext.tsx">
      {children}
    </CollectionTutorialContext.Provider>;
};
export const useCollectionTutorialsState = () => {
  const context = useContext(CollectionTutorialContext);
  if (context === undefined) {
    return {
      isTutorialOpen: false,
      tutorialsActiveView: "portfolio",
      setTutorialsActiveView: () => {},
      selectFirstItemInAssetList: false,
      setSelectFirstItemInAssetList: () => {},
      backToAssetList: false,
      setBackToAssetList: () => {},
      tutorialResultsMode: null,
      setTutorialResultsMode: () => {},
      selectFirstPathway: false,
      setSelectFirstPathway: () => {},
      viewSwitchTabs: {
        current: null
      },
      sidebarInfo: {
        current: null
      },
      sidebarDownloads: {
        current: null
      },
      pointFilters: {
        current: null
      },
      yearsPlayTrigger: {
        current: null
      },
      aggregateCompareSelector: {
        current: null
      },
      aggregateResultsPanel: {
        current: null
      },
      bindAggregateResultsPanel: () => {},
      riskAssetSearch: {
        current: null
      },
      riskItemSelect: {
        current: null
      },
      riskTabTrigger: {
        current: null
      },
      backToAssetListTrigger: {
        current: null
      },
      bindBackToAssetListTrigger: () => {},
      pointMapPanel: {
        current: null
      },
      bindPointMapPanel: () => {},
      pointMapToolbar: {
        current: null
      },
      bindPointMapToolbar: () => {},
      pointCompareToggle: {
        current: null
      },
      bindPointCompareToggle: () => {},
      adaptationTabTrigger: {
        current: null
      },
      adaptationChart: {
        current: null
      },
      bindAdaptationChart: () => {},
      adaptationListSort: {
        current: null
      },
      bindAdaptationListSort: () => {},
      adaptationRow: {
        current: null
      },
      bindAdaptationRow: () => {},
      adaptationPathwaysPanel: {
        current: null
      },
      bindAdaptationPathwaysPanel: () => {},
      adaptationPathwayCard: {
        current: null
      },
      bindAdaptationPathwayCard: () => {},
      pointDetailPanel: {
        current: null
      },
      pointSettingsPanel: {
        current: null
      },
      backToAnalysisListTrigger: {
        current: null
      }
    };
  }
  return context;
};